import React, { useEffect, useRef } from 'react';
import { Alert } from 'react-bootstrap';
import OtpInput from 'react-otp-input';
import { useTimer } from 'react-timer-hook';

type Props = {
  resendHandler?: () => void;
  otpChangeHandler: (value: string) => void;
  enableResendTimer: boolean;
  isCentered?: boolean;
  resendButtonText?: string;
  otpLabel?: string;
  ackMessage?: string;
  isDisabled?: boolean;
  ackMessageType?: 'success' | 'fail';
  otpValue: string;
  resendInterval: number;
  numInputs: number;
};

const OtpWithTimer = ({
  otpLabel,
  enableResendTimer,
  resendHandler,
  isCentered = false,
  otpChangeHandler,
  resendButtonText,
  isDisabled,
  ackMessage,
  ackMessageType,
  numInputs,
  otpValue,
  resendInterval,
}: Props) => {
  const otpRef = useRef(null);

  useEffect(() => {
    if (otpValue?.length === 0) otpRef.current.focusInput(0);
  }, [otpValue]);

  const getResendTime = () => {
    const expTime = new Date();
    expTime.setSeconds(expTime.getSeconds() + resendInterval);
    return expTime;
  };

  const { seconds, minutes, isRunning, restart } = useTimer({
    expiryTimestamp: getResendTime(),
    onExpire: () => console.log('timer expired'),
  });

  const onResendClick = () => {
    restart(getResendTime());
    resendHandler();
  };

  return (
    <>
      <div className='form-group mb-2 mx-2'>
        {otpLabel && <p className='h5 text-justify mb-2'>{otpLabel}</p>}
        <div
          className='d-flex flex-column justify-content-center'
          style={isCentered ? null : { width: 'fit-content' }}
        >
          <OtpInput
            ref={otpRef}
            isDisabled={isDisabled}
            inputStyle={{
              width: '2.3rem',
              height: '2.3rem',
              margin: '10px 0.3rem',
              fontSize: '1.2rem',
              borderRadius: '6px',
              border: '2px solid #ced4da',
              outline: 'none',
              color: '#333',
              fontWeight: '600',
              transition: 'border-color 0.2s ease',
              backgroundColor: isDisabled ? '#f8f9fa' : '#fff',
            }}
            focusStyle={{
              border: '2px solid #99834d',
              boxShadow: '0 0 0 3px rgba(153, 131, 77, 0.15)',
            }}
            containerStyle={
              isCentered ? { display: 'flex', justifyContent: 'center' } : null
            }
            value={otpValue}
            onChange={(value) => {
              otpChangeHandler(value);
            }}
            isInputNum
            isInputSecure
            numInputs={numInputs}
            shouldAutoFocus
          />

          {enableResendTimer && (
            <div className='d-flex justify-content-end mt-1'>
              {isRunning ? (
                <small className='text-muted'>
                  Resend in{' '}
                  <span style={{ color: '#99834d', fontWeight: 600 }}>
                    {`${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`}
                  </span>
                </small>
              ) : (
                <small
                  style={{
                    color: '#99834d',
                    fontWeight: 600,
                    cursor: 'pointer',
                    textDecoration: 'underline',
                  }}
                  role='button'
                  onClick={onResendClick}
                >
                  {resendButtonText}
                </small>
              )}
            </div>
          )}
        </div>

        {ackMessage && (
          <div
            style={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: '8px',
              color: ackMessageType === 'success' ? '#2e7d32' : '#c0392b',
              backgroundColor: ackMessageType === 'success' ? '#f0faf0' : '#fdf3f2',
              border: `1px solid ${ackMessageType === 'success' ? '#a5d6a7' : '#f0b4ae'}`,
              borderRadius: '6px',
              padding: '8px 12px',
              fontSize: '0.9rem',
              fontWeight: 500,
              marginTop: '8px',
              lineHeight: '1.4',
            }}
          >
            {ackMessageType === 'success' ? (
              <svg
                xmlns='http://www.w3.org/2000/svg'
                width='17'
                height='17'
                viewBox='0 0 24 24'
                fill='none'
                stroke='#2e7d32'
                strokeWidth='2.5'
                strokeLinecap='round'
                strokeLinejoin='round'
                style={{ flexShrink: 0, marginTop: '2px' }}
              >
                <circle cx='12' cy='12' r='10' />
                <polyline points='9 12 11 14 15 10' />
              </svg>
            ) : (
              <svg
                xmlns='http://www.w3.org/2000/svg'
                width='17'
                height='17'
                viewBox='0 0 24 24'
                fill='none'
                stroke='#c0392b'
                strokeWidth='2.5'
                strokeLinecap='round'
                strokeLinejoin='round'
                style={{ flexShrink: 0, marginTop: '2px' }}
              >
                <circle cx='12' cy='12' r='10' />
                <line x1='15' y1='9' x2='9' y2='15' />
                <line x1='9' y1='9' x2='15' y2='15' />
              </svg>
            )}
            <span style={{ lineHeight: '1.4' }}>{ackMessage}</span>
          </div>
        )}
      </div>
    </>
  );
};

export default React.memo(OtpWithTimer);
